import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
  Stack,
  Chip,
} from '@mui/material';
import { Add, Edit, Block, Work } from '@mui/icons-material';
import {
  useRelacionesLaboralesPorAfiliado,
  useDarDeBajaRelacionLaboral,
} from '../../../hooks/useRelacionesLaborales';
import { formatDate, formatCuit, todayLocal } from '../../../utils/format';
import {
  ESTADO_RL_LABELS,
  ESTADO_RL_COLORS,
  type RelacionLaboralExpandida,
} from '../../../types/relacionLaboral';
import RelacionLaboralDialog from './RelacionLaboralDialog';

interface Props {
  afiliadoId: string;
}

export default function TabDatosEmpleo({ afiliadoId }: Props) {
  const { data: relaciones = [], isLoading } = useRelacionesLaboralesPorAfiliado(afiliadoId);
  const dardeBaja = useDarDeBajaRelacionLaboral();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<RelacionLaboralExpandida | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (rl: RelacionLaboralExpandida) => {
    setEditing(rl);
    setDialogOpen(true);
  };

  const handleDarDeBaja = async (rl: RelacionLaboralExpandida) => {
    const today = todayLocal();
    const motivo = prompt(
      `Dar de baja la relacion con ${rl.empresa.razon_social}?\n\nMotivo (opcional):`,
      ''
    );
    if (motivo === null) return; // cancelo
    await dardeBaja.mutateAsync({
      id: rl.id,
      fecha_baja: today,
      observaciones: motivo || null,
    });
  };

  const activas = relaciones.filter((r) => r.estado === 'activa');
  const historicas = relaciones.filter((r) => r.estado === 'baja');

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Las <strong>relaciones laborales activas</strong> determinan en que
        empresas el afiliado aparece en las declaraciones juradas mensuales.
        Un afiliado puede tener mas de una relacion activa simultaneamente.
      </Alert>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          Relaciones laborales ({relaciones.length})
        </Typography>
        <Button variant="outlined" startIcon={<Add />} onClick={openCreate} size="small">
          Nueva relacion
        </Button>
      </Stack>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : relaciones.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            color: 'text.secondary',
          }}
        >
          <Work sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="body2">
            Este afiliado no tiene relaciones laborales registradas.
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Agregue una para incluirlo en las nominas mensuales de la empresa.
          </Typography>
        </Box>
      ) : (
        <>
          {activas.length > 0 && (
            <>
              <Typography variant="overline" color="text.secondary" sx={{ mt: 1 }}>
                Activas ({activas.length})
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Empresa</TableCell>
                    <TableCell>Sucursal</TableCell>
                    <TableCell>Categoria</TableCell>
                    <TableCell>Fecha alta</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activas.map((rl) => (
                    <TableRow key={rl.id} hover>
                      <TableCell>
                        <strong>{rl.empresa.razon_social}</strong>
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {formatCuit(rl.empresa.cuit)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {rl.sucursal?.denominacion ?? '—'}
                        {rl.sucursal?.domicilio && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {rl.sucursal.domicilio}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {rl.categoria ? (
                          <>
                            {rl.categoria.codigo && (
                              <strong>{rl.categoria.codigo} — </strong>
                            )}
                            {rl.categoria.descripcion}
                          </>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{formatDate(rl.fecha_alta)}</TableCell>
                      <TableCell>
                        <Chip
                          label={ESTADO_RL_LABELS[rl.estado]}
                          size="small"
                          color={ESTADO_RL_COLORS[rl.estado]}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openEdit(rl)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Dar de baja">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleDarDeBaja(rl)}
                          >
                            <Block fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}

          {historicas.length > 0 && (
            <>
              <Typography variant="overline" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
                Historicas — dadas de baja ({historicas.length})
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Empresa</TableCell>
                    <TableCell>Categoria</TableCell>
                    <TableCell>Alta</TableCell>
                    <TableCell>Baja</TableCell>
                    <TableCell>Motivo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historicas.map((rl) => (
                    <TableRow key={rl.id} sx={{ opacity: 0.75 }}>
                      <TableCell>
                        {rl.empresa.razon_social}
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {formatCuit(rl.empresa.cuit)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {rl.categoria?.descripcion ?? '—'}
                      </TableCell>
                      <TableCell>{formatDate(rl.fecha_alta)}</TableCell>
                      <TableCell>{formatDate(rl.fecha_baja)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap>
                          {rl.observaciones || '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </>
      )}

      <RelacionLaboralDialog
        open={dialogOpen}
        afiliadoId={afiliadoId}
        relacion={editing}
        onClose={() => setDialogOpen(false)}
      />
    </Box>
  );
}
